import { useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * The keyed grids' shortcuts, as data rather than as `if (event.key === ...)`.
 *
 * Two reasons this is configurable and not just a constant. The first is that
 * the people using this have muscle memory from whatever counter software they
 * used before, and every one of those binds a different key to "save" - F9 here
 * is a guess, and a wrong guess costs a keystroke on every invoice forever. The
 * second is mechanical: `F9` is a browser-level shortcut on some Linux desktops
 * and is swallowed before the page sees it, so a till that can only save with
 * F9 is a till that cannot save at all on those machines.
 *
 * Stored per DEVICE, not per user. The binding belongs to the keyboard in front
 * of someone, and the same person on the back-office machine and the counter
 * machine is keying two different layouts.
 */

export type GridAction =
  | 'nextField'
  | 'prevField'
  | 'columnDown'
  | 'columnUp'
  | 'addLine'
  | 'save'
  | 'toggleSidebar'
  | 'newBill'
  | 'newProduct';

export type Binding = {
  /** A `KeyboardEvent.key` value: 'Enter', 'F9', 'ArrowDown', 's'. */
  key: string;
  shift?: boolean;
  alt?: boolean;
  /** Ctrl on Windows and Linux, Command on a Mac - they are one flag here. */
  ctrl?: boolean;
};

export const ACTION_LABELS: Record<GridAction, { title: string; description: string }> = {
  toggleSidebar: {
    title: 'Collapse or expand the sidebar',
    description:
      'Folds the navigation down to its icons, giving the page the width, and opens it again. Works on every screen.',
  },
  nextField: {
    title: 'Next box',
    description:
      'Moves to the next box on the line, then on to the product box of the line below. Off the end of the last line it lands on the blank row waiting at the bottom; it never adds one, which is what used to leave a stack of empty rows under a finished bill.',
  },
  prevField: {
    title: 'Previous box',
    description: 'Steps back one box, and back up to the line above from the first box on a line.',
  },
  columnDown: {
    title: 'Same column, next line',
    description:
      'Walks straight down one column. This is how an invoice is checked: one figure against the same figure on the line below.',
  },
  columnUp: {
    title: 'Same column, line above',
    description: 'The same, upwards.',
  },
  addLine: {
    title: 'New line',
    description:
      'Opens a line directly below the one you are on and puts the cursor in its product box. This is the only thing that adds a line, so a bill never grows a row you did not ask for.',
  },
  save: {
    title: 'Save the invoice',
    description: 'Books the whole thing in. Only fires when every line is complete.',
  },
  newBill: {
    title: 'New bill',
    description: 'Opens the counter for a new bill, from any screen.',
  },
  newProduct: {
    title: 'Add a new product',
    description:
      'Opens the form for a product the catalogue does not have yet, from any screen. Not Ctrl+N or Ctrl+Shift+N: the browser keeps those for opening a new window, and the page never sees the keystroke.',
  },
};

export const DEFAULT_BINDINGS: Record<GridAction, Binding> = {
  /* App-wide, not a grid key: folds the sidebar to its icon rail and back. */
  toggleSidebar: { key: 'b', ctrl: true },
  nextField: { key: 'Enter' },
  prevField: { key: 'Enter', shift: true },
  /*
   * The BARE arrows walk the rows, which is what every spreadsheet and every
   * billing grid does and therefore what a hand already expects.
   *
   * They used to be Alt+arrow, because the bare arrows were stepping the
   * number under the cursor. That had it backwards: moving between lines is
   * the constant job and stepping a figure is the occasional one, so stepping
   * is the thing that moved - it is Ctrl+arrow now.
   */
  columnDown: { key: 'ArrowDown' },
  columnUp: { key: 'ArrowUp' },
  addLine: { key: 'Enter', ctrl: true },
  save: { key: 'F9' },
  /*
   * App-wide, and last, so a grid key somebody already rebound to one of these
   * keeps working: the dispatcher takes the first action that matches.
   *
   * Not Ctrl+N or Ctrl+Shift+N. Chrome and Edge keep those (and Ctrl+T,
   * Ctrl+W) for opening and closing windows, and never deliver them to a page
   * in a tab, so a binding there would open a browser window instead.
   * Ctrl+Space is the easiest reach for the action this app exists for;
   * Ctrl+Alt+N is the nearest to Ctrl+N a page can actually receive.
   */
  newBill: { key: ' ', ctrl: true },
  newProduct: { key: 'n', ctrl: true, alt: true },
};

const STORAGE_KEY = 'tlp.keybindings.v1';

/**
 * Keys that are safe to bind on their own.
 *
 * Anything else needs a modifier, and the reason is not pedantry: binding
 * "next box" to `n` means the letter n can never be typed into a batch number
 * again.
 *
 * The arrows ARE allowed on their own. They print nothing, so binding one
 * costs no character, and in a grid they are the movement key everybody
 * already reaches for.
 */
const STANDALONE_SAFE = /^(Enter|Tab|Escape|Insert|Arrow(Up|Down|Left|Right)|F[1-9]|F1[0-2])$/;

export function isBindable(binding: Binding): boolean {
  if (!binding.key) return false;
  if (binding.shift || binding.alt || binding.ctrl) {
    // A modifier is not enough on its own.
    return !/^(Shift|Alt|Control|Meta)$/.test(binding.key);
  }
  return STANDALONE_SAFE.test(binding.key);
}

/**
 * A binding in the form `aria-keyshortcuts` takes: "F2", "Alt+N". Lets a
 * screen reader announce the shortcut on the button it stands in for.
 */
export function ariaShortcut(binding: Binding): string {
  const key =
    binding.key === ' ' ? 'Space' : binding.key.length === 1 ? binding.key.toUpperCase() : binding.key;
  return [binding.ctrl && 'Control', binding.alt && 'Alt', binding.shift && 'Shift', key]
    .filter(Boolean)
    .join('+');
}

/** Two bindings that would fire on the same keystroke. */
export function sameBinding(a: Binding, b: Binding): boolean {
  return a.key === b.key && !a.shift === !b.shift && !a.alt === !b.alt && !a.ctrl === !b.ctrl;
}

/**
 * Is this the bound key?
 *
 * A letter is matched by the physical key as well as by the character. With
 * Ctrl+Alt held, Windows treats the pair as AltGr, and on many layouts the
 * character that arrives is not the letter at all ("ń", "˜", or nothing),
 * and Caps Lock turns "n" into "N". Either way the binding failed silently. The
 * key's position (`KeyN`) is the same whatever the layout or the lock.
 */
function sameKey(event: { key: string; code?: string }, key: string): boolean {
  if (event.key === key) return true;
  if (key.length !== 1 || !/[a-z]/i.test(key)) return false;
  return event.key.toLowerCase() === key.toLowerCase() || event.code === `Key${key.toUpperCase()}`;
}

/** Does this keystroke fire this binding? */
export function matches(
  event: Pick<KeyboardEvent, 'key' | 'shiftKey' | 'altKey' | 'ctrlKey' | 'metaKey'> & {
    code?: string;
  },
  binding: Binding,
): boolean {
  // Ctrl and Command are one flag, and the comparison is pulled out: written
  // inline, `&&` binds tighter than `||` and the ternary swallowed the whole
  // expression, so every binding matched every keystroke.
  const ctrlDown = event.ctrlKey || event.metaKey;
  return (
    sameKey(event, binding.key) &&
    event.shiftKey === Boolean(binding.shift) &&
    event.altKey === Boolean(binding.alt) &&
    ctrlDown === Boolean(binding.ctrl)
  );
}

const KEY_GLYPHS: Record<string, string> = {
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
  Enter: 'Enter',
  Escape: 'Esc',
  ' ': 'Space',
};

/** The caps to print, in the order a keyboard is read. */
export function bindingCaps(binding: Binding): string[] {
  const caps: string[] = [];
  if (binding.ctrl) caps.push('Ctrl');
  if (binding.alt) caps.push('Alt');
  if (binding.shift) caps.push('Shift');
  caps.push(
    KEY_GLYPHS[binding.key] ?? (binding.key.length === 1 ? binding.key.toUpperCase() : binding.key),
  );
  return caps;
}

/* ── the store ─────────────────────────────────────────────────────────────
 *
 * A module-level value plus a listener set, read through `useSyncExternalStore`
 * so every grid on screen picks up a change the moment Settings writes it -
 * without a provider wrapped round an app that has exactly one consumer.
 */

function load(): Record<GridAction, Binding> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_BINDINGS;
    const parsed = JSON.parse(raw) as Partial<Record<GridAction, Binding>>;
    /*
     * Merged over the defaults rather than trusted wholesale. A stored blob
     * from an older build is missing whatever was added since, and a grid with
     * an undefined binding is a grid where Enter does nothing.
     */
    const merged = { ...DEFAULT_BINDINGS };
    for (const action of Object.keys(DEFAULT_BINDINGS) as GridAction[]) {
      const candidate = parsed[action];
      if (candidate && typeof candidate.key === 'string' && isBindable(candidate)) {
        merged[action] = candidate;
      }
    }
    return merged;
  } catch {
    /* Private windows and blocked site data both throw here. Defaults still
       work; losing the preference is survivable, losing the grid is not. */
    return DEFAULT_BINDINGS;
  }
}

let current = load();
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

export function getBindings(): Record<GridAction, Binding> {
  return current;
}

export function setBinding(action: GridAction, binding: Binding): void {
  current = { ...current, [action]: binding };
  persist();
  emit();
}

export function resetBindings(): void {
  current = DEFAULT_BINDINGS;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* See `load`. */
  }
  emit();
}

function persist() {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
  } catch {
    /* See `load`. */
  }
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useBindings(): Record<GridAction, Binding> {
  return useSyncExternalStore(subscribe, getBindings, () => DEFAULT_BINDINGS);
}

/* ── the dispatcher ────────────────────────────────────────────────────────
 *
 * One listener on the window, and handlers that register against it by ACTION
 * and by SCOPE. Three problems made this worth building rather than writing
 * another `window.addEventListener` per page.
 *
 * The first is focus. A handler attached to a cell only ever sees keystrokes
 * aimed at that cell, and the busiest control in the purchase grid is an antd
 * `Select` that owns its own key handling and exposes none - so a shortcut
 * pressed in the product box reached nothing at all. A dispatcher reads the
 * active element instead of waiting to be told about it.
 *
 * The second is staleness. A listener registered in an effect closes over the
 * render that created it, so by the third edit it is inserting rows against a
 * `lines` array that no longer exists. Handlers are held in refs here, so a
 * registration made once always calls the current closure.
 *
 * The third is context. "New line" means one thing in the purchase grid and
 * something else on a prescription queue, and both will exist. A scope is an
 * element: the handler fires only while focus is inside it, and the innermost
 * scope wins, so a grid inside a drawer inside a page resolves the way the eye
 * would guess.
 */

type Scope = { readonly current: HTMLElement | null };

type Registration = {
  action: GridAction;
  /** Fires only while focus is inside this element. Absent means page-wide. */
  scope?: Scope;
  run: (event: KeyboardEvent) => void;
};

const registry = new Set<Registration>();

/** How deep in the document an element sits, so the innermost scope wins. */
function depthOf(element: HTMLElement): number {
  let depth = 0;
  let node: HTMLElement | null = element;
  while (node) {
    depth += 1;
    node = node.parentElement;
  }
  return depth;
}

/* ── tracing ───────────────────────────────────────────────────────────────
 *
 * What the dispatcher saw, kept in a ring buffer and shown on the Keyboard
 * settings screen.
 *
 * A shortcut that does nothing gives no clue why: the key may be unbound, the
 * handler may not be registered on this page, or focus may be somewhere the
 * handler does not cover. Those are three different faults with one symptom,
 * and the only honest way to tell them apart is to record what happened at the
 * moment the key went down.
 *
 * Off by default, kept out of production builds by nothing at all - it costs a
 * twenty-entry array and it is exactly as useful on a counter machine at four
 * in the afternoon as it is here.
 */

export type KeyTraceOutcome =
  'fired' | 'no-binding' | 'no-handler' | 'out-of-scope' | 'not-listening';

export type KeyTrace = {
  id: number;
  at: number;
  combo: string;
  action: GridAction | null;
  outcome: KeyTraceOutcome;
  /** Where focus was, in words somebody can act on. */
  focus: string;
  /** How many handlers were registered for the matched action. */
  registered: number;
};

const TRACE_KEY = 'tlp.keybindings.trace';
const TRACE_LIMIT = 20;

let traces: KeyTrace[] = [];
let traceId = 0;
const traceListeners = new Set<() => void>();

function readTracing(): boolean {
  try {
    return localStorage.getItem(TRACE_KEY) === '1';
  } catch {
    return false;
  }
}

let tracing = readTracing();

export function isTracing(): boolean {
  return tracing;
}

export function setTracing(on: boolean): void {
  tracing = on;
  try {
    if (on) localStorage.setItem(TRACE_KEY, '1');
    else localStorage.removeItem(TRACE_KEY);
  } catch {
    /* Private windows throw. Tracing still works for this session. */
  }
  if (!on) traces = [];
  syncListener();
  emitTrace();
}

export function clearTraces(): void {
  traces = [];
  emitTrace();
}

function emitTrace() {
  for (const listener of traceListeners) listener();
}

function record(entry: Omit<KeyTrace, 'id' | 'at'>) {
  traceId += 1;
  traces = [{ ...entry, id: traceId, at: Date.now() }, ...traces].slice(0, TRACE_LIMIT);
  emitTrace();
}

/** The keystroke as a person would say it. */
function comboOf(event: KeyboardEvent): string {
  const parts: string[] = [];
  if (event.ctrlKey || event.metaKey) parts.push('Ctrl');
  if (event.altKey) parts.push('Alt');
  if (event.shiftKey) parts.push('Shift');
  parts.push(
    KEY_GLYPHS[event.key] ?? (event.key.length === 1 ? event.key.toUpperCase() : event.key),
  );
  return parts.join(' + ');
}

/** Where focus is, named the way the screen names it. */
function describeFocus(element: Element | null): string {
  if (!element || element === document.body) return 'nothing focused (page body)';
  const html = element as HTMLElement;
  const row = html.closest<HTMLElement>('[data-row]')?.dataset.row;
  const field = html.dataset.nav ?? (html.closest('.ant-select') ? 'product' : null);
  const where = row === undefined ? null : `line ${Number(row) + 1}`;
  if (field && where) return `${field}, ${where}`;
  if (where) return where;
  if (field) return field;
  return html.tagName.toLowerCase();
}

export function subscribeTraces(listener: () => void): () => void {
  traceListeners.add(listener);
  return () => traceListeners.delete(listener);
}

export function getTraces(): KeyTrace[] {
  return traces;
}

/**
 * The actions something on this page is listening for.
 *
 * The other half of a shortcut that does nothing: before asking why a key did
 * not fire, it is worth knowing whether anything on the screen was waiting for
 * it. A purchase shortcut pressed on the settings screen is not broken, it is
 * simply somewhere else.
 */
export function registeredActions(): GridAction[] {
  const seen = new Set<GridAction>();
  for (const registration of registry) seen.add(registration.action);
  return [...seen];
}

/* ── the dispatcher ───────────────────────────────────────────────────────*/

function dispatch(event: KeyboardEvent) {
  /*
   * A modifier pressed on its own is somebody still reaching for the real key.
   * It can never match a binding - `isBindable` refuses one - and recording it
   * filled the diagnostics with "Ctrl + Control" rows that hid the keystroke
   * being looked for.
   */
  if (
    event.key === 'Control' ||
    event.key === 'Shift' ||
    event.key === 'Alt' ||
    event.key === 'Meta'
  ) {
    return;
  }

  const bindings = getBindings();
  /*
   * Exactly one action can own a keystroke: `matches` compares every modifier,
   * so Ctrl+Enter and Enter are different keys rather than one with a flag,
   * and the settings screen refuses to save a second action onto a combination
   * that is already taken.
   */
  const action =
    (Object.keys(bindings) as GridAction[]).find((candidate) =>
      matches(event, bindings[candidate]),
    ) ?? null;

  if (!action) {
    /* Only worth recording a modified key; logging every letter typed into a
       batch number would bury the thing being looked for. */
    if (tracing && (event.ctrlKey || event.metaKey || event.altKey || event.key.length > 1)) {
      record({
        combo: comboOf(event),
        action: null,
        outcome: 'no-binding',
        focus: describeFocus(document.activeElement),
        registered: 0,
      });
    }
    return;
  }

  const active = document.activeElement;
  let best: Registration | undefined;
  let bestDepth = -1;
  let fallback: Registration | undefined;
  let registered = 0;

  for (const registration of registry) {
    if (registration.action !== action) continue;
    registered += 1;
    const element = registration.scope?.current;
    if (!element) {
      fallback ??= registration;
      continue;
    }
    if (!active || !element.contains(active)) continue;
    const depth = depthOf(element);
    if (depth > bestDepth) {
      bestDepth = depth;
      best = registration;
    }
  }

  const chosen = best ?? fallback;

  if (tracing) {
    record({
      combo: comboOf(event),
      action,
      outcome: chosen ? 'fired' : registered === 0 ? 'no-handler' : 'out-of-scope',
      focus: describeFocus(active),
      registered,
    });
  }

  if (!chosen) return;
  chosen.run(event);
}

let attached = false;

/**
 * The listener is on while anything needs it: a registered handler, or tracing.
 *
 * Tracing has to keep it alive on its own, otherwise the one case worth
 * diagnosing - a page where nothing registered the shortcut - would record
 * nothing and look identical to a page where the key is simply wrong.
 */
/*
 * Capture phase, on the window.
 *
 * Bubbling was not enough. A shortcut has to survive every component between
 * the focused element and the window, and any one of them calling
 * `stopPropagation` on the native event - which rc-select, rc-picker and antd
 * modals all do for their own keys - silently deletes it. Capture runs on the
 * way DOWN, before any of them, so a binding cannot be swallowed by a control
 * that happens to have focus.
 *
 * The cost is that this sees the key first and must be careful: it only acts
 * on combinations somebody deliberately bound, and anything it does not
 * recognise it leaves entirely alone.
 */
function syncListener() {
  const wanted = registry.size > 0 || tracing;
  if (wanted && !attached) {
    window.addEventListener('keydown', dispatch, true);
    attached = true;
  } else if (!wanted && attached) {
    window.removeEventListener('keydown', dispatch, true);
    attached = false;
  }
}

if (typeof window !== 'undefined' && tracing) syncListener();

function register(registration: Registration): () => void {
  registry.add(registration);
  syncListener();
  return () => {
    registry.delete(registration);
    syncListener();
  };
}

/**
 * Run `handler` when the key bound to `action` is pressed.
 *
 * `scope` is a ref to the element the shortcut belongs to; the handler fires
 * only while focus is inside it. Leave it out for a page-wide shortcut such as
 * save. `enabled` unregisters rather than guarding inside the handler, so a
 * disabled shortcut cannot swallow a keystroke a parent scope wanted.
 *
 *   useKeyAction('addLine', () => addLineAfter(grid.getActive().row), { scope: grid.ref });
 *
 * The handler may be a fresh closure every render; it is held in a ref, so
 * there is no dependency array to get wrong and no stale state to insert
 * against.
 */
export function useKeyAction(
  action: GridAction,
  handler: (event: KeyboardEvent) => void,
  options: { scope?: Scope; enabled?: boolean } = {},
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const { scope, enabled = true } = options;

  useEffect(() => {
    if (!enabled) return;
    return register({
      action,
      scope,
      run: (event) => handlerRef.current(event),
    });
  }, [action, scope, enabled]);
}

/** The recent keystrokes the dispatcher saw. Empty unless tracing is on. */
export function useKeyTraces(): KeyTrace[] {
  return useSyncExternalStore(subscribeTraces, getTraces, () => EMPTY_TRACES);
}

const EMPTY_TRACES: KeyTrace[] = [];
